import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Save, Mail, MessageSquare, User, DollarSign, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [notes, setNotes] = useState({ general: '', scholarship: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, [userId]);

  const loadUser = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setNotes({
          general: data.general_notes || '',
          scholarship: data.scholarship_notes || ''
        });
      }
    } catch (error) {
      toast.error('Error loading user');
    }
    setLoading(false);
  };

  const saveNotes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/user/${userId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(notes)
      });

      if (response.ok) {
        toast.success('Notes saved!');
        loadUser();
      }
    } catch (error) {
      toast.error('Error saving notes');
    }
  };

  const sendEmail = async () => {
    const subject = prompt('Email subject:');
    const message = prompt('Email message:');
    
    if (!subject || !message) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-user-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          email: user.email,
          subject,
          message
        })
      });

      if (response.ok) {
        toast.success('Email sent!');
      }
    } catch (error) {
      toast.error('Error sending email');
    }
  };

  const resetPassword = async () => {
    if (!confirm(`Reset password for ${user.full_name}? They will receive an email with the new password.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-user-password/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });

      if (response.ok) {
        toast.success('Password reset! User will receive email.');
      }
    } catch (error) {
      toast.error('Error resetting password');
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  if (!user) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">User not found</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button onClick={() => navigate('/admin')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          <h1 className="text-3xl font-bold">{user.full_name}</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                User Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{user.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Attending</p>
                <p className="font-semibold">{user.attending}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">In-Person</p>
                <p className="font-semibold">{user.in_person ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Registered</p>
                <p className="font-semibold">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </CardContent>
          </Card>

          {/* Payment Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Price Selected</p>
                <p className="font-semibold">{user.price_selected}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Status</p>
                <p className="font-semibold">{user.payment_status || 'Pending'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-semibold">{user.payment_method || 'Not provided'}</p>
              </div>
            </CardContent>
          </Card>

          {/* General Notes */}
          <Card>
            <CardHeader>
              <CardTitle>General Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={notes.general}
                onChange={(e) => setNotes({...notes, general: e.target.value})}
                placeholder="Add notes about this user..."
                className="min-h-[150px]"
              />
            </CardContent>
          </Card>

          {/* Scholarship Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Scholarship Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={notes.scholarship}
                onChange={(e) => setNotes({...notes, scholarship: e.target.value})}
                placeholder="Notes about scholarship request..."
                className="min-h-[150px]"
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-3">
              <Button onClick={saveNotes} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Save Notes
              </Button>
              <Button onClick={sendEmail} className="bg-blue-600 hover:bg-blue-700">
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700">
                <MessageSquare className="h-4 w-4 mr-2" />
                Send Message
              </Button>
              <Button onClick={resetPassword} className="bg-red-600 hover:bg-red-700">
                🔑 Reset Password
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
